import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
  arrayUnion
} from 'firebase/firestore';
import { db } from '../firebase';
import { Player, Product } from '../types';
import { createDebitReceipt, getReceiptsByUser } from './receiptService';
import { getUserById } from './userService';
import { syncPlayerToAlgolia, deletePlayerFromAlgolia, isAlgoliaConfigured } from './algoliaService';

const COLLECTION_NAME = 'players';

// Helper function to fetch guardian names from guardian IDs
const getGuardianNames = async (guardianIds: string[] | undefined): Promise<string[]> => {
  if (!guardianIds || guardianIds.length === 0) return [];

  const names: string[] = [];
  for (const guardianId of guardianIds) {
    try {
      const guardian = await getUserById(guardianId);
      names.push(guardian?.name || 'Guardian');
    } catch {
      names.push('Guardian');
    }
  }
  return names;
};

// Helper function to calculate all invoice dates from a start date up to the current date
// Returns an array of dates for which receipts should be created
const calculateAllInvoiceDatesFromStart = (
  startDate: Date,
  recurringDuration: { value: number; unit: 'days' | 'weeks' | 'months' | 'years' },
  deadlineDays: number
): Array<{ invoiceDate: Date; deadlineDate: Date }> => {
  const now = new Date();
  now.setHours(23, 59, 59, 999); // End of today to include today's date

  const invoiceDates: Array<{ invoiceDate: Date; deadlineDate: Date }> = [];
  let currentDate = new Date(startDate);
  currentDate.setHours(0, 0, 0, 0);

  // Generate all invoice dates from start until we pass today
  while (currentDate <= now) {
    const invoiceDate = new Date(currentDate);
    const deadlineDate = new Date(currentDate);
    deadlineDate.setDate(deadlineDate.getDate() + deadlineDays);

    invoiceDates.push({ invoiceDate, deadlineDate });

    // Calculate next date based on recurring duration
    switch (recurringDuration.unit) {
      case 'days':
        currentDate.setDate(currentDate.getDate() + recurringDuration.value);
        break;
      case 'weeks':
        currentDate.setDate(currentDate.getDate() + (recurringDuration.value * 7));
        break;
      case 'months':
        currentDate.setMonth(currentDate.getMonth() + recurringDuration.value);
        break;
      case 'years':
        currentDate.setFullYear(currentDate.getFullYear() + recurringDuration.value);
        break;
    }
  }

  return invoiceDates;
};

// Helper function to calculate the next future invoice date from the last generated date
const calculateNextFutureInvoiceDate = (
  lastInvoiceDate: Date,
  recurringDuration: { value: number; unit: 'days' | 'weeks' | 'months' | 'years' }
): Date => {
  const nextDate = new Date(lastInvoiceDate);

  switch (recurringDuration.unit) {
    case 'days':
      nextDate.setDate(nextDate.getDate() + recurringDuration.value);
      break;
    case 'weeks':
      nextDate.setDate(nextDate.getDate() + (recurringDuration.value * 7));
      break;
    case 'months':
      nextDate.setMonth(nextDate.getMonth() + recurringDuration.value);
      break;
    case 'years':
      nextDate.setFullYear(nextDate.getFullYear() + recurringDuration.value);
      break;
  }

  return nextDate;
};

// Helper function to calculate the earliest nextReceiptDate from assigned products
const calculateEarliestReceiptDate = (assignedProducts: Player['assignedProducts']): Timestamp | null => {
  if (!assignedProducts || assignedProducts.length === 0) {
    return null;
  }

  let earliest: Date | null = null;

  // Check all active products with invoiceDate
  const activeProducts = assignedProducts.filter(
    ap => ap.status === 'active' && ap.invoiceDate
  );

  for (const product of activeProducts) {
    const invoiceDate = product.invoiceDate.toDate();
    if (!earliest || invoiceDate < earliest) {
      earliest = invoiceDate;
    }
  }

  return earliest ? Timestamp.fromDate(earliest) : null;
};

export const createPlayer = async (playerData: Omit<Player, 'createdAt' | 'updatedAt'>): Promise<Player> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, playerData.id);
    const player: Player = {
      ...playerData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    await setDoc(docRef, player);

    // Sync to Algolia
    if (isAlgoliaConfigured()) {
      try {
        const user = await getUserById(player.userId);
        if (user) {
          const guardianNames = await getGuardianNames(player.guardianId);
          await syncPlayerToAlgolia(player, {
            name: user.name,
            email: user.email,
            phone: user.phone,
            photoURL: user.photoURL
          }, undefined, guardianNames);
          console.log('✅ Player synced to Algolia');
        }
      } catch (algoliaError) {
        console.error('Warning: Failed to sync player to Algolia:', algoliaError);
        // Don't fail player creation if Algolia sync fails
      }
    }

    return player;
  } catch (error) {
    console.error('Error creating player:', error);
    throw error;
  }
};

// Create a player and optionally assign a product with automatic receipt generation
export const createPlayerWithProduct = async (
  playerData: Omit<Player, 'createdAt' | 'updatedAt'>,
  product?: Product
): Promise<Player> => {
  try {
    // First create the player
    const player = await createPlayer(playerData);
    
    // If a product is provided, assign it and create a debit receipt
    if (product) {
      console.log('Creating player with product assignment:', { playerId: player.id, productId: product.id });
      
      // Assign the product and create debit receipt
      await assignProductToPlayer(
        player.id,
        product,
        player.organizationId,
        player.academyId[0] // Use first academy if multiple
      );
      
      console.log('Player created with product assignment and receipt generated');
    }
    
    return player;
  } catch (error) {
    console.error('Error creating player with product:', error);
    // If product assignment fails, we still want to keep the player
    // The admin can manually assign products later
    throw error;
  }
};

export const getPlayerById = async (playerId: string): Promise<Player | null> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, playerId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data() as Player;
    }
    return null;
  } catch (error) {
    console.error('Error getting player:', error);
    throw error;
  }
};

export const getPlayerByUserId = async (userId: string): Promise<Player | null> => {
  try {
    const q = query(collection(db, COLLECTION_NAME), where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      return querySnapshot.docs[0].data() as Player;
    }
    return null;
  } catch (error) {
    console.error('Error getting player by user ID:', error);
    throw error;
  }
};

export const getPlayersByGuardianId = async (guardianId: string): Promise<Player[]> => {
  try {
    console.log('🔎 getPlayersByGuardianId: Searching for players with guardianId containing:', guardianId);
    const q = query(collection(db, COLLECTION_NAME), where('guardianId', 'array-contains', guardianId));
    const querySnapshot = await getDocs(q);

    const players = querySnapshot.docs.map(doc => {
      const playerData = doc.data() as Player;
      console.log('🎯 Found player:', playerData.userId, 'with guardianIds:', playerData.guardianId);
      return playerData;
    });

    console.log('📊 getPlayersByGuardianId result:', players.length, 'players found');
    return players;
  } catch (error) {
    console.error('❌ Error getting players by guardian ID:', error);
    throw error;
  }
};

// Batch load players by multiple user IDs (more efficient than individual calls)
export const getPlayersByUserIds = async (userIds: string[]): Promise<Player[]> => {
  try {
    if (userIds.length === 0) return [];

    // Firestore 'in' query supports up to 30 items, so we batch if needed
    const batchSize = 30;
    const allPlayers: Player[] = [];

    for (let i = 0; i < userIds.length; i += batchSize) {
      const batch = userIds.slice(i, i + batchSize);
      const q = query(collection(db, COLLECTION_NAME), where('userId', 'in', batch));
      const querySnapshot = await getDocs(q);

      const players = querySnapshot.docs.map(doc => doc.data() as Player);
      allPlayers.push(...players);
    }

    return allPlayers;
  } catch (error) {
    console.error('Error getting players by user IDs:', error);
    throw error;
  }
};

export const getPlayersByOrganization = async (organizationId: string): Promise<Player[]> => {
  try {
    const q = query(collection(db, COLLECTION_NAME), where('organizationId', '==', organizationId));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => doc.data() as Player);
  } catch (error) {
    console.error('Error getting players by organization:', error);
    throw error;
  }
};

export const getPlayersByAcademy = async (academyId: string): Promise<Player[]> => {
  try {
    const q = query(collection(db, COLLECTION_NAME), where('academyId', 'array-contains', academyId));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => doc.data() as Player);
  } catch (error) {
    console.error('Error getting players by academy:', error);
    throw error;
  }
};

export const updatePlayer = async (playerId: string, updates: Partial<Player>): Promise<void> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, playerId);

    // Get current player data to check for academyId changes
    const playerSnap = await getDoc(docRef);
    const currentPlayer = playerSnap.exists() ? playerSnap.data() as Player : null;

    console.log('🔄 updatePlayer: Updating player', playerId);
    console.log('🔄 updatePlayer: Current academyId:', currentPlayer?.academyId);
    console.log('🔄 updatePlayer: New academyId in updates:', updates.academyId);
    console.log('🔄 updatePlayer: Full updates object:', JSON.stringify(updates, null, 2));

    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });

    console.log('✅ updatePlayer: Player document updated successfully');

    // Verify the update was applied
    const verifySnap = await getDoc(docRef);
    const verifiedPlayer = verifySnap.exists() ? verifySnap.data() as Player : null;
    console.log('🔍 updatePlayer: Verified academyId after update:', verifiedPlayer?.academyId);

    // If academyId is being updated, also update the user's roles
    if (updates.academyId && currentPlayer) {
      try {
        const userRef = doc(db, 'users', currentPlayer.userId);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();
          const roles = userData.roles || [];

          // Find the player role for this organization and update its academyId
          const updatedRoles = roles.map((role: any) => {
            if (role.organizationId === currentPlayer.organizationId && role.role?.includes('player')) {
              return {
                ...role,
                academyId: updates.academyId
              };
            }
            return role;
          });

          await updateDoc(userRef, {
            roles: updatedRoles,
            updatedAt: Timestamp.now()
          });
          console.log('✅ User roles updated with new academyId');
        }
      } catch (userError) {
        console.error('Warning: Failed to update user academyId:', userError);
      }

      // Update receipts with the new academyId so finance data follows the player
      try {
        const newAcademyId = Array.isArray(updates.academyId) ? updates.academyId[0] : updates.academyId;
        if (newAcademyId) {
          const userReceipts = await getReceiptsByUser(currentPlayer.userId);
          const receiptsToUpdate = userReceipts.filter(r =>
            r.organizationId === currentPlayer.organizationId &&
            r.status !== 'deleted' &&
            r.academyId !== newAcademyId
          );

          if (receiptsToUpdate.length > 0) {
            const userRef = doc(db, 'users', currentPlayer.userId);
            await Promise.all(
              receiptsToUpdate.map(receipt =>
                updateDoc(doc(userRef, 'receipts', receipt.id), {
                  academyId: newAcademyId,
                  updatedAt: Timestamp.now()
                })
              )
            );
            console.log(`✅ Updated ${receiptsToUpdate.length} receipts with new academyId: ${newAcademyId}`);
          }
        }
      } catch (receiptError) {
        console.error('Warning: Failed to update receipts with new academyId:', receiptError);
      }
    }

    // Sync to Algolia
    if (isAlgoliaConfigured()) {
      try {
        const updatedPlayer = await getPlayerById(playerId);
        if (updatedPlayer) {
          const user = await getUserById(updatedPlayer.userId);
          if (user) {
            const guardianNames = await getGuardianNames(updatedPlayer.guardianId);
            await syncPlayerToAlgolia(updatedPlayer, {
              name: user.name,
              email: user.email,
              phone: user.phone,
              photoURL: user.photoURL
            }, undefined, guardianNames);
            console.log('✅ Player updates synced to Algolia');
          }
        }
      } catch (algoliaError) {
        console.error('Warning: Failed to sync player updates to Algolia:', algoliaError);
      }
    }
  } catch (error) {
    console.error('Error updating player:', error);
    throw error;
  }
};

export const deletePlayer = async (playerId: string): Promise<void> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, playerId);

    // Read the player doc first so we can cascade-delete receipts afterwards
    const playerSnap = await getDoc(docRef);
    const playerData = playerSnap.exists() ? (playerSnap.data() as Player) : null;

    await deleteDoc(docRef);

    // Delete from Algolia
    if (isAlgoliaConfigured()) {
      try {
        await deletePlayerFromAlgolia(playerId);
        console.log('✅ Player deleted from Algolia');
      } catch (algoliaError) {
        console.error('Warning: Failed to delete player from Algolia:', algoliaError);
      }
    }

    // Cascade: delete org-scoped receipts for this player and recalculate stats
    if (playerData?.userId && playerData?.organizationId) {
      try {
        const { deletePlayerReceipts } = await import('./receiptService');
        const { deletedCount } = await deletePlayerReceipts(playerData.userId, playerData.organizationId);
        console.log(`🧾 Cascade-deleted ${deletedCount} receipts for player ${playerId}`);
      } catch (receiptError) {
        console.error('Warning: Failed to cascade-delete player receipts:', receiptError);
      }

      try {
        const { recalculateAllStats } = await import('./statsService');
        await recalculateAllStats(playerData.organizationId);
        console.log('📊 Stats recalculated after player deletion');
      } catch (statsError) {
        console.error('Warning: Failed to recalculate stats after player deletion:', statsError);
      }
    }
  } catch (error) {
    console.error('Error deleting player:', error);
    throw error;
  }
};

// Assign a product to a player and create a debit receipt
export const assignProductToPlayer = async (
  playerId: string,
  product: Product,
  organizationId: string,
  academyId?: string,
  invoiceDate?: Date,
  deadlineDate?: Date,
  invoiceGeneration?: 'immediate' | 'scheduled',
  invoiceDay?: number, // Day of month: 1-31, or -1 for last day of month
  deadlineDay?: number,
  discount?: { type: 'percentage' | 'fixed'; value: number; reason?: string }
): Promise<void> => {
  try {
    console.log('🎯 assignProductToPlayer: Starting with:', {
      playerId,
      productId: product.id,
      organizationId,
      academyId,
      productType: product.productType,
      invoiceGeneration
    });

    // Get current player data
    const playerRef = doc(db, COLLECTION_NAME, playerId);
    console.log('🔍 assignProductToPlayer: Getting player document:', playerRef.path);

    const playerSnap = await getDoc(playerRef);

    if (!playerSnap.exists()) {
      console.error('❌ assignProductToPlayer: Player document not found:', playerId);
      throw new Error('Player not found');
    }

    const player = playerSnap.data() as Player;
    console.log('✅ assignProductToPlayer: Player document found:', {
      playerId: player.id,
      userId: player.userId,
      organizationId: player.organizationId
    });

    // Use provided dates or set defaults
    const now = new Date();
    const finalInvoiceDate = invoiceDate || now;
    const finalDeadlineDate = deadlineDate || new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000); // Default 30 days
    const finalInvoiceGeneration = invoiceGeneration || 'immediate';

    // Get user reference for the receipt
    const userRef = doc(db, 'users', player.userId);
    const productRef = doc(db, 'products', product.id);

    // Determine if we should track this product in assignedProducts
    // Only track: recurring products OR one-time scheduled products
    // Don't track: one-time immediate products (just create receipt and done)
    const isOneTimeImmediate = product.productType === 'one-time' && finalInvoiceGeneration === 'immediate';
    const shouldTrackInAssignedProducts = !isOneTimeImmediate;

    console.log('📋 assignProductToPlayer: Product tracking decision:', {
      productType: product.productType,
      invoiceGeneration: finalInvoiceGeneration,
      isOneTimeImmediate,
      shouldTrackInAssignedProducts
    });

    if (shouldTrackInAssignedProducts) {
      // Check if product is already assigned (only for tracked products)
      const existingAssignment = player.assignedProducts?.find(p => p.productId === product.id);
      if (existingAssignment && existingAssignment.status === 'active') {
        console.log('⚠️ assignProductToPlayer: Product already assigned to this player, skipping');
        throw new Error(`Product "${product.name}" is already assigned to this player`);
      }

      // Add product to player's assigned products
      const assignedProduct: any = {
        productId: product.id,
        productName: product.name,
        price: product.price,
        assignedDate: Timestamp.now(),
        status: 'active' as const,
        invoiceDate: Timestamp.fromDate(finalInvoiceDate),
        deadlineDate: Timestamp.fromDate(finalDeadlineDate),
        invoiceDay: invoiceDay || 1, // Default to 1st of month
        deadlineDay: deadlineDay || 30, // Default to 30 days after invoice
        receiptStatus: finalInvoiceGeneration === 'immediate' ? 'immediate' as const : 'scheduled' as const,
        productType: product.productType || 'one-time',
      };

      // Add recurring duration if it's a recurring product
      if (product.productType === 'recurring' && product.recurringDuration) {
        assignedProduct.recurringDuration = product.recurringDuration;
      }

      // Add discount if provided
      if (discount && discount.value > 0) {
        assignedProduct.discount = discount;
      }

      const updatedAssignedProducts = player.assignedProducts ?
        [...player.assignedProducts.filter(p => p.productId !== product.id), assignedProduct] :
        [assignedProduct];

      console.log('📋 assignProductToPlayer: Updated assigned products:', updatedAssignedProducts);

      // Update player document with assigned product
      await updateDoc(playerRef, {
        assignedProducts: updatedAssignedProducts,
        updatedAt: Timestamp.now()
      });
      console.log('✅ assignProductToPlayer: Player assigned products updated');

      // Update product's linkedPlayerIds and linkedPlayerNames (only for tracked products)
      try {
        const user = await getUserById(player.userId);
        const playerName = user?.name || `Player ${player.userId}`;

        const productDoc = await getDoc(productRef);
        if (productDoc.exists()) {
          const productData = productDoc.data();
          const currentLinkedIds = productData.linkedPlayerIds || [];

          if (!currentLinkedIds.includes(player.userId)) {
            await updateDoc(productRef, {
              linkedPlayerIds: arrayUnion(player.userId),
              linkedPlayerNames: arrayUnion(playerName),
              updatedAt: Timestamp.now()
            });
            console.log('✅ assignProductToPlayer: Product updated with linked player:', playerName);
          }
        }
      } catch (productUpdateError) {
        console.error('⚠️ assignProductToPlayer: Failed to update product linkedPlayerIds:', productUpdateError);
      }

      // Handle scheduled receipts for recurring products (no immediate receipt creation)
      if (finalInvoiceGeneration === 'scheduled' && product.productType === 'recurring') {
        console.log('📅 assignProductToPlayer: Scheduling receipt for recurring product...');

        // Calculate player-level nextReceiptDate using invoiceDay
        const earliestReceiptDate = calculateEarliestReceiptDate(updatedAssignedProducts);

        await updateDoc(playerRef, {
          nextReceiptDate: earliestReceiptDate,
          updatedAt: Timestamp.now()
        });

        console.log('✅ assignProductToPlayer: Player nextReceiptDate set to:', earliestReceiptDate?.toDate().toLocaleDateString());
      }

      // Handle scheduled receipts for one-time products (no immediate receipt creation)
      if (finalInvoiceGeneration === 'scheduled' && product.productType === 'one-time') {
        console.log('📅 assignProductToPlayer: Scheduling receipt for one-time product...');

        // For one-time scheduled products, use the invoiceDate directly
        // Compare with current player nextReceiptDate and use the earliest
        const playerDoc = await getDoc(playerRef);
        const currentNextReceiptDate = playerDoc.data()?.nextReceiptDate;

        let newNextReceiptDate: Timestamp;
        if (currentNextReceiptDate) {
          // Use the earlier of current nextReceiptDate or this product's invoiceDate
          newNextReceiptDate = finalInvoiceDate < currentNextReceiptDate.toDate()
            ? Timestamp.fromDate(finalInvoiceDate)
            : currentNextReceiptDate;
        } else {
          newNextReceiptDate = Timestamp.fromDate(finalInvoiceDate);
        }

        await updateDoc(playerRef, {
          nextReceiptDate: newNextReceiptDate,
          updatedAt: Timestamp.now()
        });

        console.log('✅ assignProductToPlayer: Player nextReceiptDate set to:', newNextReceiptDate.toDate().toLocaleDateString());
      }

      // Create receipts for recurring products with immediate generation
      // This handles both current and past dates (creating multiple receipts if start date is in the past)
      if (finalInvoiceGeneration === 'immediate' && product.productType === 'recurring') {
        console.log('🧾 assignProductToPlayer: Creating receipts for recurring product...');

        try {
          if (!product.recurringDuration) {
            throw new Error('Recurring product must have a recurring duration');
          }

          // Calculate all invoice dates from the start date until today
          const allInvoiceDates = calculateAllInvoiceDatesFromStart(
            finalInvoiceDate,
            product.recurringDuration,
            deadlineDay || 30
          );

          console.log(`📅 assignProductToPlayer: Found ${allInvoiceDates.length} invoice date(s) to create receipts for`);

          // Calculate discounted price if discount is provided
          let finalPrice = product.price;
          let originalPrice: number | undefined;
          let discountApplied: string | undefined;

          if (discount && discount.value > 0) {
            originalPrice = product.price;
            if (discount.type === 'percentage') {
              finalPrice = product.price * (1 - discount.value / 100);
              discountApplied = `${discount.value}% discount${discount.reason ? ` (${discount.reason})` : ''}`;
            } else {
              finalPrice = product.price - discount.value;
              discountApplied = `${discount.value} fixed discount${discount.reason ? ` (${discount.reason})` : ''}`;
            }
            finalPrice = Math.max(0, finalPrice);
            console.log(`💰 Discount applied: ${discountApplied}, original: ${originalPrice}, final: ${finalPrice}`);
          }

          // Create a receipt for each invoice date
          let lastCreatedInvoiceDate = finalInvoiceDate;
          for (const { invoiceDate, deadlineDate } of allInvoiceDates) {
            const receipt = await createDebitReceipt(
              userRef,
              productRef,
              {
                name: product.name,
                price: finalPrice,
                invoiceDate: invoiceDate,
                deadline: deadlineDate,
                originalPrice,
                discountApplied
              },
              organizationId,
              academyId
            );
            console.log(`🎉 assignProductToPlayer: Receipt created for ${invoiceDate.toLocaleDateString()}:`, receipt.id);
            lastCreatedInvoiceDate = invoiceDate;
          }

          // Calculate the next future invoice date
          const nextInvoiceDate = calculateNextFutureInvoiceDate(
            lastCreatedInvoiceDate,
            product.recurringDuration
          );
          const nextDeadlineDate = new Date(nextInvoiceDate.getTime() + (deadlineDay || 30) * 24 * 60 * 60 * 1000);

          console.log(`📅 assignProductToPlayer: Next invoice date will be: ${nextInvoiceDate.toLocaleDateString()}`);

          // Update assignedProduct with next invoiceDate and deadlineDate
          const currentAssignedProducts = (await getDoc(playerRef)).data()?.assignedProducts || [];
          const updatedWithNextDates = currentAssignedProducts.map((ap: any) =>
            ap.productId === product.id ? {
              ...ap,
              invoiceDate: Timestamp.fromDate(nextInvoiceDate), // Update to next invoice date
              deadlineDate: Timestamp.fromDate(nextDeadlineDate) // Update deadline accordingly
            } : ap
          );

          // Calculate the earliest nextReceiptDate for player-level field
          const earliestReceiptDate = calculateEarliestReceiptDate(updatedWithNextDates);

          await updateDoc(playerRef, {
            assignedProducts: updatedWithNextDates,
            nextReceiptDate: earliestReceiptDate,
            updatedAt: Timestamp.now()
          });

          console.log('✅ assignProductToPlayer: Created', allInvoiceDates.length, 'receipt(s)');
          console.log('✅ assignProductToPlayer: Next invoiceDate set to:', nextInvoiceDate.toLocaleDateString());
          console.log('✅ assignProductToPlayer: Player nextReceiptDate set to:', earliestReceiptDate?.toDate().toLocaleDateString());
        } catch (receiptError: any) {
          console.error('❌ assignProductToPlayer: Failed to create debit receipt:', receiptError);
          throw new Error(`Failed to create receipt: ${receiptError?.message || receiptError}`);
        }
      }
    } else {
      // One-time immediate product: Just create the receipt, don't track
      console.log('🧾 assignProductToPlayer: One-time immediate product - creating receipt only (not tracking in assignedProducts)');

      try {
        // Calculate discounted price if discount is provided
        let oneTimePrice = product.price;
        let oneTimeOriginalPrice: number | undefined;
        let oneTimeDiscountApplied: string | undefined;

        if (discount && discount.value > 0) {
          oneTimeOriginalPrice = product.price;
          if (discount.type === 'percentage') {
            oneTimePrice = product.price * (1 - discount.value / 100);
            oneTimeDiscountApplied = `${discount.value}% discount${discount.reason ? ` (${discount.reason})` : ''}`;
          } else {
            oneTimePrice = product.price - discount.value;
            oneTimeDiscountApplied = `${discount.value} fixed discount${discount.reason ? ` (${discount.reason})` : ''}`;
          }
          oneTimePrice = Math.max(0, oneTimePrice);
        }

        const receipt = await createDebitReceipt(
          userRef,
          productRef,
          {
            name: product.name,
            price: oneTimePrice,
            invoiceDate: finalInvoiceDate,
            deadline: finalDeadlineDate,
            originalPrice: oneTimeOriginalPrice,
            discountApplied: oneTimeDiscountApplied
          },
          organizationId,
          academyId
        );

        console.log('🎉 assignProductToPlayer: One-time receipt created:', receipt.id);
        console.log('📍 Receipt location: users/' + player.userId + '/receipts/' + receipt.id);
      } catch (receiptError: any) {
        console.error('❌ assignProductToPlayer: Failed to create debit receipt:', receiptError);
        throw new Error(`Failed to create receipt: ${receiptError?.message || receiptError}`);
      }
    }

  } catch (error) {
    console.error('Error assigning product to player:', error);
    throw error;
  }
};

// Remove a product assignment from a player
export const removeProductFromPlayer = async (
  playerId: string,
  productId: string
): Promise<void> => {
  try {
    const playerRef = doc(db, COLLECTION_NAME, playerId);
    const playerSnap = await getDoc(playerRef);

    if (!playerSnap.exists()) {
      throw new Error('Player not found');
    }

    const player = playerSnap.data() as Player;

    if (!player.assignedProducts) {
      throw new Error('No products assigned to this player');
    }

    // Remove the product from the assigned products array
    // Note: Active receipts are preserved - they remain as outstanding invoices
    const updatedAssignedProducts = player.assignedProducts.filter(p => p.productId !== productId);

    // Recalculate player-level nextReceiptDate
    const earliestReceiptDate = calculateEarliestReceiptDate(updatedAssignedProducts);

    await updateDoc(playerRef, {
      assignedProducts: updatedAssignedProducts,
      nextReceiptDate: earliestReceiptDate,
      updatedAt: Timestamp.now()
    });

    console.log('Product removed from player:', playerId, 'product:', productId);
    console.log('Player nextReceiptDate updated to:', earliestReceiptDate?.toDate().toLocaleDateString() || 'null');

  } catch (error) {
    console.error('Error removing product from player:', error);
    throw error;
  }
};

// Get active products assigned to a player
export const getPlayerActiveProducts = async (playerId: string): Promise<Player['assignedProducts']> => {
  try {
    const player = await getPlayerById(playerId);
    if (!player) {
      throw new Error('Player not found');
    }

    return player.assignedProducts?.filter(p => p.status === 'active') || [];
  } catch (error) {
    console.error('Error getting player active products:', error);
    throw error;
  }
};

// Sync all player-product links to update product linkedPlayerIds
// This is a one-time utility to fix products that were linked before the fix
export const syncProductLinkedPlayers = async (organizationId: string): Promise<{ synced: number; errors: number }> => {
  try {
    console.log('🔄 syncProductLinkedPlayers: Starting sync for organization:', organizationId);

    // Get all players in the organization
    const playersQuery = query(
      collection(db, COLLECTION_NAME),
      where('organizationId', '==', organizationId)
    );
    const playersSnapshot = await getDocs(playersQuery);
    const players = playersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Player);

    console.log(`📋 Found ${players.length} players in organization`);

    // Build a map of productId -> { userIds: [], names: [] }
    const productPlayerMap: Record<string, { userIds: string[]; names: string[] }> = {};

    for (const player of players) {
      if (!player.assignedProducts || player.assignedProducts.length === 0) continue;

      // Get user name
      const user = await getUserById(player.userId);
      const playerName = user?.name || `Player ${player.userId}`;

      for (const assignedProduct of player.assignedProducts) {
        if (assignedProduct.status !== 'active') continue;

        const productId = assignedProduct.productId;
        if (!productPlayerMap[productId]) {
          productPlayerMap[productId] = { userIds: [], names: [] };
        }

        // Add if not already in the list
        if (!productPlayerMap[productId].userIds.includes(player.userId)) {
          productPlayerMap[productId].userIds.push(player.userId);
          productPlayerMap[productId].names.push(playerName);
        }
      }
    }

    console.log(`📦 Found ${Object.keys(productPlayerMap).length} products with linked players`);

    // Update each product
    let synced = 0;
    let errors = 0;

    for (const [productId, playerData] of Object.entries(productPlayerMap)) {
      try {
        const productRef = doc(db, 'products', productId);
        await updateDoc(productRef, {
          linkedPlayerIds: playerData.userIds,
          linkedPlayerNames: playerData.names,
          updatedAt: Timestamp.now()
        });
        synced++;
        console.log(`✅ Updated product ${productId} with ${playerData.userIds.length} players`);
      } catch (error) {
        console.error(`❌ Failed to update product ${productId}:`, error);
        errors++;
      }
    }

    console.log(`🎉 Sync complete. Synced: ${synced}, Errors: ${errors}`);
    return { synced, errors };
  } catch (error) {
    console.error('Error syncing product linked players:', error);
    throw error;
  }
};

// Migration utility to clean up old player data and ensure correct invoiceDate values
// This removes lastGeneratedDate field and ensures invoiceDate is properly set
export const migratePlayerScheduledData = async (organizationId: string): Promise<{
  playersUpdated: number;
  productsFixed: number;
  errors: number;
  details: { playerId: string; status: 'success' | 'skipped' | 'error'; message: string }[];
}> => {
  try {
    console.log('🔄 migratePlayerScheduledData: Starting migration for organization:', organizationId);

    const playersQuery = query(
      collection(db, COLLECTION_NAME),
      where('organizationId', '==', organizationId)
    );
    const playersSnapshot = await getDocs(playersQuery);
    const players = playersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    console.log(`📋 Found ${players.length} players to check`);

    const results = {
      playersUpdated: 0,
      productsFixed: 0,
      errors: 0,
      details: [] as { playerId: string; status: 'success' | 'skipped' | 'error'; message: string }[]
    };

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    for (const playerData of players) {
      try {
        const playerRef = doc(db, COLLECTION_NAME, playerData.id);
        const updates: any = {};
        let needsUpdate = false;
        let productsFixedForPlayer = 0;

        // Check if lastGeneratedDate exists and remove it
        if ('lastGeneratedDate' in playerData) {
          updates.lastGeneratedDate = null; // Firestore will delete the field when set to null with deleteField()
          needsUpdate = true;
          console.log(`🗑️ Player ${playerData.id}: Removing lastGeneratedDate field`);
        }

        // Check and fix assignedProducts invoiceDate
        const assignedProducts = (playerData as any).assignedProducts || [];
        if (assignedProducts.length > 0) {
          const fixedProducts = assignedProducts.map((product: any) => {
            // Skip if product is not active
            if (product.status !== 'active') {
              return product;
            }

            // Check if invoiceDate needs to be set or fixed
            const invoiceDate = product.invoiceDate;
            const isInvoiceDateMissing = !invoiceDate;
            const isInvoiceDateInPast = invoiceDate && invoiceDate.toDate() < now;

            // Check if receiptStatus needs to be set for recurring products
            const isRecurring = product.productType === 'recurring';
            const needsReceiptStatus = isRecurring && !product.receiptStatus;

            // Fix if:
            // 1. Recurring product missing receiptStatus (set to 'scheduled')
            // 2. Scheduled product with missing or past invoiceDate
            // 3. Recurring product with missing invoiceDate (even if receiptStatus not set yet)
            const needsInvoiceDateFix = (isInvoiceDateMissing || isInvoiceDateInPast) &&
              (product.receiptStatus === 'scheduled' || isRecurring);

            if (needsReceiptStatus || needsInvoiceDateFix) {
              // Calculate next invoice date based on invoiceDay and recurringDuration
              let nextInvoiceDate = new Date(now);

              if (product.invoiceDay) {
                // Use invoiceDay to set the day of the month
                const invoiceDay = product.invoiceDay === -1
                  ? new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate() // Last day of month
                  : Math.min(product.invoiceDay, new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate());

                nextInvoiceDate.setDate(invoiceDay);

                // If we're past that day this month, move to next month
                if (nextInvoiceDate <= now) {
                  nextInvoiceDate.setMonth(nextInvoiceDate.getMonth() + 1);
                  // Recalculate for the new month
                  const newInvoiceDay = product.invoiceDay === -1
                    ? new Date(nextInvoiceDate.getFullYear(), nextInvoiceDate.getMonth() + 1, 0).getDate()
                    : Math.min(product.invoiceDay, new Date(nextInvoiceDate.getFullYear(), nextInvoiceDate.getMonth() + 1, 0).getDate());
                  nextInvoiceDate.setDate(newInvoiceDay);
                }
              } else if (product.recurringDuration) {
                // Calculate based on recurring duration from now
                switch (product.recurringDuration.unit) {
                  case 'days':
                    nextInvoiceDate.setDate(now.getDate() + product.recurringDuration.value);
                    break;
                  case 'weeks':
                    nextInvoiceDate.setDate(now.getDate() + (product.recurringDuration.value * 7));
                    break;
                  case 'months':
                    nextInvoiceDate.setMonth(now.getMonth() + product.recurringDuration.value);
                    break;
                  case 'years':
                    nextInvoiceDate.setFullYear(now.getFullYear() + product.recurringDuration.value);
                    break;
                }
              } else {
                // Default to 1st of next month
                nextInvoiceDate.setMonth(now.getMonth() + 1);
                nextInvoiceDate.setDate(1);
              }

              // Calculate deadline date
              const deadlineDays = product.deadlineDay || 30;
              const nextDeadlineDate = new Date(nextInvoiceDate);
              nextDeadlineDate.setDate(nextDeadlineDate.getDate() + deadlineDays);

              const fixes: string[] = [];
              if (needsReceiptStatus) fixes.push('receiptStatus');
              if (needsInvoiceDateFix) fixes.push('invoiceDate');
              console.log(`📅 Player ${playerData.id}: Fixed product ${product.productName} - ${fixes.join(', ')} -> invoiceDate: ${nextInvoiceDate.toLocaleDateString()}`);

              productsFixedForPlayer++;
              return {
                ...product,
                invoiceDate: Timestamp.fromDate(nextInvoiceDate),
                deadlineDate: Timestamp.fromDate(nextDeadlineDate),
                // Set receiptStatus to 'scheduled' for recurring products if missing
                ...(needsReceiptStatus && { receiptStatus: 'scheduled' as const })
              };
            }

            return product;
          });

          if (productsFixedForPlayer > 0) {
            updates.assignedProducts = fixedProducts;
            needsUpdate = true;
          }
        }

        if (needsUpdate) {
          // Use updateDoc with FieldValue.delete() for lastGeneratedDate
          const { deleteField } = await import('firebase/firestore');

          if ('lastGeneratedDate' in playerData) {
            updates.lastGeneratedDate = deleteField();
          }

          updates.updatedAt = Timestamp.now();

          await updateDoc(playerRef, updates);

          results.playersUpdated++;
          results.productsFixed += productsFixedForPlayer;
          results.details.push({
            playerId: playerData.id,
            status: 'success',
            message: `Updated: removed lastGeneratedDate=${('lastGeneratedDate' in playerData)}, fixed ${productsFixedForPlayer} products`
          });
        } else {
          results.details.push({
            playerId: playerData.id,
            status: 'skipped',
            message: 'No changes needed'
          });
        }
      } catch (error: any) {
        console.error(`❌ Error migrating player ${playerData.id}:`, error);
        results.errors++;
        results.details.push({
          playerId: playerData.id,
          status: 'error',
          message: error?.message || 'Unknown error'
        });
      }
    }

    console.log(`🎉 Migration complete. Players updated: ${results.playersUpdated}, Products fixed: ${results.productsFixed}, Errors: ${results.errors}`);
    return results;
  } catch (error) {
    console.error('Error in migratePlayerScheduledData:', error);
    throw error;
  }
};